import { useEffect, useRef } from 'react';
import { Infobulle } from './Infobulle';

/**
 * Une zone de saisie a mise en forme LEGERE (2026-10-31) : gras,
 * italique, souligne, alignement, paragraphes. C'est le commentaire de
 * la fiche adherent ; rien de plus n'est prevu — pas de liste, pas de
 * lien, pas d'image — et le serveur retire tout ce qui depasse.
 *
 * Elle s'appuie sur l'edition native du navigateur (`contentEditable`
 * et `document.execCommand`) : c'est deprecie sur le papier mais present
 * dans tous les navigateurs, et pour cinq commandes une bibliotheque
 * d'editeur pese plus que l'application. Les raccourcis Ctrl+B, Ctrl+I
 * et Ctrl+U sont ceux du navigateur.
 *
 * Le composant est CONTROLE par sa valeur initiale seulement : rejouer
 * `innerHTML` a chaque frappe deplacerait le curseur. Il ne reecrit le
 * contenu que si la valeur recue differe de ce qu'il affiche, ce qui
 * n'arrive qu'a l'ouverture ou a une remise a zero.
 *
 * NE PAS LE PLACER DANS UN `<label>` (donc pas dans `Champ`) : un clic
 * dans la zone de texte activerait le premier controle du label — le
 * bouton Gras —, et tout ce qu'on tape ensuite sort en gras. Vecu le
 * 2026-10-31, pris d'abord pour un style de frappe collant de Chrome.
 */
export function EditeurTexteLeger({
  valeur,
  onChange,
  id,
  lignes = 6,
}: {
  valeur: string;
  onChange: (html: string) => void;
  id?: string;
  /** Hauteur minimale, en lignes de texte. */
  lignes?: number;
}) {
  const zone = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = zone.current;
    if (element && element.innerHTML !== valeur) element.innerHTML = valeur;
  }, [valeur]);

  const commande = (nom: string) => {
    zone.current?.focus();
    // Des balises plutot que des styles en ligne : c'est ce que le
    // serveur garde (b, i, u, p), un style serait retire.
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(nom);
    if (zone.current) onChange(zone.current.innerHTML);
  };

  const boutons: { nom: string; libelle: string; titre: string; classe?: string }[] = [
    { nom: 'bold', libelle: 'G', titre: 'Gras (Ctrl+B)', classe: 'font-bold' },
    { nom: 'italic', libelle: 'I', titre: 'Italique (Ctrl+I)', classe: 'italic' },
    { nom: 'underline', libelle: 'S', titre: 'Souligné (Ctrl+U)', classe: 'underline' },
    { nom: 'justifyLeft', libelle: '≡', titre: 'Aligner à gauche' },
    { nom: 'justifyCenter', libelle: '☰', titre: 'Centrer' },
    { nom: 'justifyRight', libelle: '≡', titre: 'Aligner à droite' },
  ];

  return (
    <div className="rounded-md border border-encre-300 focus-within:border-encre-500">
      <div className="flex flex-wrap gap-1 border-b border-encre-200 bg-encre-50 px-2 py-1">
        {boutons.map((b, i) => (
          <Infobulle key={b.nom} texte={b.titre} className={`inline-flex ${i === 3 ? 'ml-2' : ''}`}>
            <button
              type="button"
              aria-label={b.titre}
              className={`h-7 min-w-7 rounded px-2 text-sm text-encre-700 hover:bg-encre-200 ${b.classe ?? ''} ${
                b.nom === 'justifyRight' ? 'scale-x-[-1]' : ''
              }`}
              // `preventDefault` : un clic sur le bouton ne doit pas
              // prendre le focus, sinon la selection a mettre en forme
              // disparait avant la commande.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commande(b.nom)}
            >
              {b.libelle}
            </button>
          </Infobulle>
        ))}
      </div>
      <div
        ref={zone}
        id={id}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        className="texte-riche px-3 py-2 text-sm outline-none"
        style={{ minHeight: `${lignes * 1.5}rem` }}
        onInput={() => zone.current && onChange(zone.current.innerHTML)}
        onFocus={() => {
          // Entree cree un paragraphe, pas un <div> : c'est la balise
          // que le serveur conserve telle quelle.
          document.execCommand('defaultParagraphSeparator', false, 'p');
          // Une zone vide recoit un paragraphe neuf, curseur dedans : la
          // premiere ligne a ainsi la meme forme que les suivantes.
          const element = zone.current;
          if (element && element.innerHTML.trim() === '') {
            element.innerHTML = '<p><br></p>';
            const selection = window.getSelection();
            const plage = document.createRange();
            plage.setStart(element.firstChild!, 0);
            plage.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(plage);
          }
        }}
        onPaste={(e) => {
          // Coller en TEXTE : ce qui vient d'un traitement de texte ou
          // d'une page web arrive charge de styles et de balises que le
          // serveur retirerait de toute facon. Les retours a la ligne
          // deviennent des paragraphes.
          e.preventDefault();
          const texte = e.clipboardData.getData('text/plain');
          const html = texte
            .split(/\r?\n/)
            .map((l) => l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
            .join('<br>');
          document.execCommand('insertHTML', false, html);
          if (zone.current) onChange(zone.current.innerHTML);
        }}
      />
    </div>
  );
}
